import { __assign } from "tslib";
import * as React from 'react';
import { Typography, makeStyles, ThemeProvider, Chip } from '@material-ui/core';
import { useField } from 'formik';
import { BLayout } from '../BLayout';
import { BError } from '../input/BError';
var useStyles = makeStyles({
    chip: {
        margin: 2
    }
});
/**
 * **Must be a child of BForm**
 *
 * BChipGroup is, visually, a collection of values that, when true, are visible and can be interacted with.
 * Primarily this interaction is turning those booleans to false on click (thus rendering again with those
 * clicked chips invisible).
 *
 * Note that you can only define one onDelete and onClick for the whole group.
 * If you've got some chips with a lot of different functionality, consider using BChipButtons instead.
 *
 *  **Required Props:**
 *  - name
 *   - must match the name from initialValues
 *
 * **Optional Props:**
 * - [BLayout](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/update-docs/documentations/tag-references/MWidget.md)
 *  - custom gridding, sizes, and alignment
 * - deleteIcon
 *  - Override the default delete icon element. Shown only if onDelete is set.
 * - onDelete
 *  - Callback function fired when the delete icon is clicked. If set, the delete icon will be shown. (Same for all buttons)
 * - onClick
 *  - Callback function fired when the button is clicked.
 * - size
 *  - The size of the chip.
 * - label
 *  - acts as the title for all chips
 *
 * **Basic Example:**
 * ```jsx
 * <BChipGroup
 *    name="chips"
 *    label="All chips"
 *    options={[{ name: "chip1", label: "one" }, { name: "chip2", label: "two" },]}
 *    onDelete={(name) => { console.log(`${name} chip deleted`) }}
 *  />
 * ```
 *
 * Demos:
 *
 * - [Sample Form](https://material-ui.com/components/text-fields/)
 *
 * API:
 *
 * - [BChipGroup API](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/development/docs/tag-references/input/BTextField.md)
 */
export var BChipGroup = function (props) {
    var name = props.name, onClick = props.onClick, onDelete = props.onDelete, deleteIcon = props.deleteIcon, _a = props.label, label = _a === void 0 ? '' : _a, _b = props.options, options = _b === void 0 ? [] : _b, _c = props.size, size = _c === void 0 ? 'small' : _c, _d = props.w, w = _d === void 0 ? 3 : _d, className = props.className;
    var classes = useStyles(ThemeProvider);
    // Formik boilerplate -----------------------
    var _e = useField(name), field = _e[0], helpers = _e[2];
    var setValue = helpers.setValue;
    var value = field.value;
    // ------------------------------------------
    var onDeleteWrapped = function (myName) {
        var _a;
        setValue(__assign(__assign({}, value), (_a = {}, _a[myName] = false, _a)));
        if (onDelete && typeof onDelete !== 'boolean')
            onDelete(name);
    };
    var items = [];
    options.forEach(function (o, i) {
        if (value[o.name]) {
            items.push(React.createElement(Chip, __assign({}, o.chipProps, { id: o.name + "-chip", className: classes.chip, label: o.label, onDelete: onDelete ? function () { return onDeleteWrapped(o.name); } : undefined, onClick: onClick ? function () { return onClick(o.name); } : undefined, clickable: onClick !== undefined, color: o.color, size: size, deleteIcon: deleteIcon, key: "chip-" + i + "-" + o.name })));
        }
    });
    return (React.createElement(BLayout, { w: w },
        React.createElement("div", { className: className },
            React.createElement(Typography, { gutterBottom: true, color: 'textSecondary' }, label),
            items,
            React.createElement(BError, { name: name, id: name + "-err" }))));
};
//# sourceMappingURL=BChipGroup.js.map